import React, {useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Flex,
    Link,
    Text,
    VStack,
    Image,
    useMultiStyleConfig,
    useDisclosure,
    useBreakpointValue,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerCloseButton,
    DrawerBody,
    /*     Progress, */
    DrawerFooter,
    Divider,
} from '@chakra-ui/react'
import {FormattedMessage, useIntl} from 'react-intl'
import EnergyLabel from '../../energy-label'
import ColorSwatch from '../../product-tile/partials/color-swatch'
import QuantityPicker from '../../quantity-picker'
import {EditIcon} from '../../icons'
import {remCalc} from '../../../theme/_utils'
import FlexibleBundleProgress from './flexible-bundle-progress'
import BundleProductRow from './bundle-product-row'

const AccessoryTile = ({
    accessory,
    variant = 'add', // 'add' o 'quantity'
    quantity = 0,
    selectedQuantity = 0,
    canAddMore = true,
    onAdd = () => {},
    /*     onIncrease = () => {},
    onDecrease = () => {}, */
    onRemove = () => {},
    onQuantityChange = () => {},
}) => {
    const style = useMultiStyleConfig('Bundle')
    const accessoryStock = Number(accessory?.stock || 0)
    const isAccessoryOrderable = accessory?.orderable && accessoryStock > 0
    const hasReachedAccessoryStock = selectedQuantity >= accessoryStock
    const isAddDisabled = !isAccessoryOrderable || !canAddMore || hasReachedAccessoryStock
    return (
        <Flex {...style.accessoryTileContainer}>
            {/* Left Block: Image + Name + Price */}
            <Flex {...style.accessoryTileLeftBlock}>
                <Flex {...style.accessoryImageWrapper}>
                    {accessory.image && (
                        <Image
                            src={accessory.image}
                            alt={accessory.productName}
                            {...style.accessoryImage}
                        />
                    )}
                </Flex>
                <VStack {...style.accessoryInfoBlock} align="flex-start">
                    <Flex {...style.accessoryInfoContainer}>
                        <Text {...style.accessoryName}>{accessory.c_variantIdentifier}</Text>
                        <Text {...style.accessorySubtitle}>{accessory.c_productSubtitle}</Text>
                    </Flex>
                    <Text {...style.accessoryPrice}>+€{accessory.price}</Text>
                </VStack>
            </Flex>

            {/* Right Block: Variant Button */}
            {variant === 'add' ? (
                <Button
                    variant="outline"
                    onClick={() => onAdd(accessory)}
                    isDisabled={isAddDisabled}
                    {...style.accessoryAddButton}
                >
                    <FormattedMessage id="flexible_bundle.add_button" defaultMessage="Add" />
                </Button>
            ) : (
                <QuantityPicker
                    key={`${accessory.productId}-${quantity}`}
                    value={quantity}
                    min={1}
                    max={99}
                    clampValueOnBlur={true}
                    onChange={(stringValue, numberValue) => {
                        if (!Number.isFinite(numberValue) || numberValue < 1) {
                            return
                        }

                        if (numberValue === quantity) {
                            return
                        }

                        onQuantityChange(accessory.productId, numberValue)
                    }}
                    onRemoveItemClick={() => onRemove(accessory.productId)}
                    productName={accessory.productName}
                    hideIncDecButtons={false}
                />
            )}
        </Flex>
    )
}
AccessoryTile.propTypes = {
    accessory: PropTypes.object.isRequired,
    variant: PropTypes.oneOf(['add', 'quantity']),
    quantity: PropTypes.number,
    onAdd: PropTypes.func,
    /*     onIncrease: PropTypes.func,
    onDecrease: PropTypes.func, */
    onRemove: PropTypes.func,
    onQuantityChange: PropTypes.func,
    selectedQuantity: PropTypes.number,
    canAddMore: PropTypes.bool,
}

const FlexibleBundle = ({
    flexibleBundleInfo,
    selectedAccessories = [],
    onSelectAccessories = () => {},
    onFocusLinkClick,
}) => {
    const style = useMultiStyleConfig('Bundle')
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [drawerSelectedAccessories, setDrawerSelectedAccessories] = useState([])
    const shouldInsetDrawer = useBreakpointValue({base: false, lg: true})
    const STALE_POST_ADD_SUPPRESSION_WINDOW_MS = 150
    const ACCIDENTAL_REMOVE_GUARD_MS = 250
    const pendingAddSyncByProductRef = useRef({})
    const lastQuantityChangeAtByProductRef = useRef({})
    const intl = useIntl()

    const primaryProducts = flexibleBundleInfo?.primaryProducts || []
    const optionalProducts = flexibleBundleInfo?.optionalProducts || []
    const bundleMaxSelectable = flexibleBundleInfo?.bundleMaxSelectable || 0
    const totalSelectedQuantity = useMemo(() => {
        return drawerSelectedAccessories.reduce((sum, item) => {
            return sum + item.quantity
        }, 0)
    }, [drawerSelectedAccessories])

    const selectedCount = useMemo(() => {
        return selectedAccessories.reduce((sum, item) => {
            return sum + item.quantity
        }, 0)
    }, [selectedAccessories])

    const isAccessoriesFullySelected =
        bundleMaxSelectable > 0 && selectedCount === bundleMaxSelectable

    const optionalProductsCount = optionalProducts.length
    const canAddMore = totalSelectedQuantity < bundleMaxSelectable

    if (!flexibleBundleInfo) {
        return null
    }
    const getTotalSelectedQuantity = (items) => {
        return items.reduce((sum, item) => sum + item.quantity, 0)
    }
    const handleOpenDrawer = () => {
        setDrawerSelectedAccessories(selectedAccessories)
        onOpen()
    }

    const getSafeNextQuantity = (items, productId, requestedQuantity) => {
        const currentItem = items.find((item) => item.productId === productId)

        if (!currentItem) {
            return null
        }

        const accessoryStock = Number(currentItem.stock || 0)
        const isAccessoryOrderable = currentItem.orderable && accessoryStock > 0

        if (!isAccessoryOrderable) {
            return null
        }

        const otherItemsQuantity = items.reduce((sum, item) => {
            if (item.productId === productId) {
                return sum
            }

            return sum + item.quantity
        }, 0)

        const maxAllowedByBundle = Math.max(bundleMaxSelectable - otherItemsQuantity, 1)
        const maxAllowed = Math.min(accessoryStock, maxAllowedByBundle)
        const parsedRequestedQuantity = Number(requestedQuantity)

        if (!Number.isFinite(parsedRequestedQuantity)) {
            return null
        }

        return Math.max(1, Math.min(parsedRequestedQuantity, maxAllowed))
    }

    const handleAddAccessory = (accessory) => {
        const accessoryStock = Number(accessory?.stock || 0)
        const isAccessoryOrderable = accessory?.orderable && accessoryStock > 0

        if (!isAccessoryOrderable) {
            return
        }

        setDrawerSelectedAccessories((prev) => {
            const totalSelectedQuantity = getTotalSelectedQuantity(prev)

            const existingItem = prev.find((item) => item.productId === accessory.productId)

            if (existingItem) {
                const safeNextQuantity = getSafeNextQuantity(
                    prev,
                    accessory.productId,
                    existingItem.quantity + 1,
                )

                if (safeNextQuantity == null || safeNextQuantity === existingItem.quantity) {
                    return prev
                }

                pendingAddSyncByProductRef.current[accessory.productId] = {
                    quantity: safeNextQuantity,
                    ts: Date.now(),
                }

                return prev.map((item) =>
                    item.productId === accessory.productId
                        ? {...item, quantity: safeNextQuantity}
                        : item,
                )
            }

            if (totalSelectedQuantity >= bundleMaxSelectable) {
                return prev
            }

            return [
                ...prev,
                {
                    ...accessory,
                    quantity: 1,
                },
            ]
        })
    }

    /*     const handleIncreaseQuantity = (productId) => {
        console.log('handleIncreaseQuantity')
        setDrawerSelectedAccessories((prev) => {
            const totalSelectedQuantity = getTotalSelectedQuantity(prev)

            if (totalSelectedQuantity >= bundleMaxSelectable) {
                return prev
            }

            return prev.map((item) => {
                if (item.productId !== productId) {
                    return item
                }

                const accessoryStock = Number(item.stock || 0)
                const isAccessoryOrderable = item.orderable && accessoryStock > 0

                if (!isAccessoryOrderable || item.quantity >= accessoryStock) {
                    return item
                }

                return {
                    ...item,
                    quantity: item.quantity + 1,
                }
            })
        })
    }

    const handleDecreaseQuantity = (productId) => {
        console.log('handleDecreaseQuantity')
        setDrawerSelectedAccessories(
            drawerSelectedAccessories.map((item) => {
                if (item.productId !== productId) {
                    return item
                }

                return {
                    ...item,
                    quantity: Math.max(1, item.quantity - 1),
                }
            }),
        )
    } */
    const handleChangeAccessoryQuantity = (productId, nextQuantity) => {
        setDrawerSelectedAccessories((prev) => {
            const pendingAddSync = pendingAddSyncByProductRef.current[productId]
            const pendingAddSyncQuantity = pendingAddSync?.quantity
            const pendingAddSyncTs = pendingAddSync?.ts
            const parsedNextQuantity = Number(nextQuantity)
            const isWithinSuppressionWindow =
                Number.isFinite(pendingAddSyncTs) &&
                Date.now() - pendingAddSyncTs < STALE_POST_ADD_SUPPRESSION_WINDOW_MS

            if (
                isWithinSuppressionWindow &&
                Number.isFinite(pendingAddSyncQuantity) &&
                Number.isFinite(parsedNextQuantity) &&
                parsedNextQuantity < pendingAddSyncQuantity
            ) {
                delete pendingAddSyncByProductRef.current[productId]
                return prev
            }

            if (
                Number.isFinite(pendingAddSyncQuantity) &&
                parsedNextQuantity >= pendingAddSyncQuantity
            ) {
                delete pendingAddSyncByProductRef.current[productId]
            }

            if (
                Number.isFinite(pendingAddSyncQuantity) &&
                Number.isFinite(pendingAddSyncTs) &&
                !isWithinSuppressionWindow
            ) {
                delete pendingAddSyncByProductRef.current[productId]
            }

            const safeNextQuantity = getSafeNextQuantity(prev, productId, nextQuantity)

            const currentItem = prev.find((item) => item.productId === productId)

            if (safeNextQuantity == null) {
                return prev
            }

            if (!currentItem || currentItem.quantity === safeNextQuantity) {
                return prev
            }

            lastQuantityChangeAtByProductRef.current[productId] = Date.now()

            return prev.map((item) =>
                item.productId === productId ? {...item, quantity: safeNextQuantity} : item,
            )
        })
    }

    const handleRemoveAccessory = (productId) => {
        const lastQuantityChangeAt = lastQuantityChangeAtByProductRef.current[productId]

        if (
            Number.isFinite(lastQuantityChangeAt) &&
            Date.now() - lastQuantityChangeAt < ACCIDENTAL_REMOVE_GUARD_MS
        ) {
            return
        }

        setDrawerSelectedAccessories((prev) => prev.filter((item) => item.productId !== productId))
    }

    const handleDone = () => {
        onSelectAccessories(drawerSelectedAccessories)
        onClose()
    }

    const handleCancel = () => {
        setDrawerSelectedAccessories(selectedAccessories)
        onClose()
    }

    return (
        <Flex {...style.flexibleBundleContainer}>
            {/* IN THIS BUNDLE SECTION */}
            {primaryProducts.length > 0 && (
                <Flex {...style.primaryProductSection}>
                    <Text {...style.inThisBundleTitle}>
                        <FormattedMessage
                            id="flexible_bundle.in_this_bundle"
                            defaultMessage="In this bundle"
                        />
                    </Text>

                    <VStack {...style.primaryProductContainer}>
                        {primaryProducts.map((primaryProduct) => (
                            <Flex key={primaryProduct.productId} {...style.primaryProductCard}>
                                <Flex {...style.primaryProductContent}>
                                    {/* Product Image */}
                                    <Box {...style.imageWrapper}>
                                        {primaryProduct.image && (
                                            <Image
                                                src={primaryProduct.image}
                                                alt={primaryProduct.productName}
                                                {...style.productImage}
                                            />
                                        )}
                                    </Box>

                                    {/* Product Info */}
                                    <VStack {...style.primaryProductInfo}>
                                        <Flex {...style.primaryProductNameContainer}>
                                            <Text {...style.productName}>
                                                {primaryProduct.c_variantIdentifier}
                                            </Text>
                                            <Flex {...style.primaryProductSubtitleContainer}>
                                                {primaryProduct.c_hexColour && (
                                                    <Flex {...style.colorSwatchWrapper}>
                                                        <ColorSwatch
                                                            enabledVariants={[primaryProduct]} // Pass the primary product as an array to ColorSwatch, since it expects an array of variants
                                                            isBundle={true} // Indicate that this is a bundle context
                                                        />
                                                    </Flex>
                                                )}
                                                {primaryProduct.c_productSubtitle && (
                                                    <Text {...style.productSubtitle}>
                                                        {primaryProduct.c_productSubtitle}
                                                    </Text>
                                                )}
                                            </Flex>
                                        </Flex>
                                        {/* Primary Product(s) Information Badge */}
                                        {primaryProduct?.c_energyEfficiencyClass && (
                                            <Flex {...style.energyLabelContainer}>
                                                <EnergyLabel
                                                    level={primaryProduct?.c_energyEfficiencyClass}
                                                    manualDocument={
                                                        primaryProduct?.c_manualDocument
                                                    }
                                                    iconSize={'small'}
                                                    width=" 38.214px"
                                                    labelPosition="top"
                                                />
                                                <Button
                                                    variant="link-button"
                                                    size="base"
                                                    onClick={() =>
                                                        onFocusLinkClick?.('editorial-key-features')
                                                    }
                                                    sx={{
                                                        marginLeft: remCalc(12),
                                                        textStyle: 'body-xsmall',
                                                        color: 'foreground.neutral.muted',
                                                    }}
                                                >
                                                    {intl.formatMessage({
                                                        defaultMessage: 'Product information',
                                                        id: 'energy_label.product_information',
                                                    })}
                                                </Button>
                                            </Flex>
                                        )}
                                    </VStack>
                                </Flex>
                            </Flex>
                        ))}
                    </VStack>
                </Flex>
            )}

            {/* ACCESSORIES SECTION */}
            {optionalProductsCount > 0 && (
                <VStack {...style.optionalProductsSection}>
                    <Text {...style.accessoriesTitle}>
                        <FormattedMessage
                            id="flexible_bundle.accessories"
                            defaultMessage="Accessories"
                        />
                    </Text>
                    <Flex
                        {...style.optionalProductsOutterContainer}
                        {...(isAccessoriesFullySelected
                            ? style.optionalProductsOutterContainerSelected
                            : {})}
                    >
                        <Flex {...style.optionalProductsInnerContainer}>
                            <Flex
                                {...style.optionalProductsHeader}
                                {...(selectedCount > 0 ? style.optionalProductsHeaderSelected : {})}
                            >
                                <Flex {...style.optionalProductsHeaderContent}>
                                    <Text {...style.selectAccessoriesTitle}>
                                        <FormattedMessage
                                            id="flexible_bundle.select_accessories_title"
                                            defaultMessage="Select your free accessories"
                                        />
                                    </Text>
                                    <Text {...style.selectedAccessoriesCount}>
                                        <FormattedMessage
                                            id="flexible_bundle.selected_count"
                                            defaultMessage="{count}/{max} selected"
                                            values={{
                                                count: selectedCount,
                                                max: bundleMaxSelectable,
                                            }}
                                        />
                                    </Text>
                                </Flex>
                                <Flex
                                    {...style.optionalProductsHeaderAction}
                                    {...(selectedCount > 0
                                        ? style.optionalProductsHeaderActionSelected
                                        : {})}
                                >
                                    {selectedCount === 0 ? (
                                        <Button
                                            variant="outline"
                                            onClick={handleOpenDrawer}
                                            {...style.optionalProductsHeaderActionButton}
                                        >
                                            <FormattedMessage
                                                id="flexible_bundle.select_accessories_button"
                                                defaultMessage="Select Accessories"
                                            />
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="ghost"
                                            onClick={handleOpenDrawer}
                                            leftIcon={<EditIcon boxSize={remCalc(16)} />}
                                            {...style.optionalProductsHeaderActionButton}
                                            {...style.optionalProductsHeaderActionButtonSelected}
                                        >
                                            <FormattedMessage
                                                id="flexible_bundle.edit_accessories_button"
                                                defaultMessage="Edit"
                                            />
                                        </Button>
                                    )}
                                </Flex>
                            </Flex>
                            {/* {selectedAccessories.length > 0 && (
                                <VStack {...style.confirmedAccessoriesListContainer}>
                                    <Flex {...style.confirmedAccessoriesList}>
                                        {selectedAccessories.map((accessory) => (
                                            <Flex
                                                {...style.selectedProductRow}
                                                key={accessory.productId}
                                            >
                                                <Flex {...style.selectedProductInfoContainer}>
                                                    <Flex {...style.selectedProductImageContainer}>
                                                        {accessory.image && (
                                                            <Image
                                                                src={accessory.image}
                                                                alt={accessory.productName}
                                                                {...style.selectedProductImage}
                                                            />
                                                        )}
                                                    </Flex>

                                                    <Flex {...style.selectedProductInfo}>
                                                        {accessory?.productName && (
                                                            <Text {...style.productName}>
                                                                {accessory.c_variantIdentifier}
                                                            </Text>
                                                        )}
                                                        {accessory?.c_productSubtitle && (
                                                            <Text {...style.productSubtitle}>
                                                                {accessory.c_productSubtitle}
                                                            </Text>
                                                        )}
                                                    </Flex>
                                                </Flex>
                                                <Flex {...style.selectedProductPriceContainer}>
                                                    <Text {...style.confirmedAccessoryPrice}>
                                                        {accessory.quantity > 1
                                                            ? `${accessory.quantity}x `
                                                            : ''}
                                                        {Number(accessory.price || 0).toFixed(2)} €
                                                    </Text>
                                                </Flex>
                                            </Flex>
                                        ))}
                                    </Flex>
                                </VStack>
                            )} */}
                            {selectedAccessories.length > 0 && (
                                <VStack {...style.confirmedAccessoriesListContainer}>
                                    <Flex {...style.confirmedAccessoriesList}>
                                        {selectedAccessories.map((accessory) => (
                                            <BundleProductRow
                                                key={accessory.productId}
                                                product={accessory}
                                                quantity={accessory.quantity}
                                                showQuantity={true}
                                            />
                                        ))}
                                    </Flex>
                                </VStack>
                            )}
                        </Flex>
                    </Flex>
                </VStack>
            )}
            {/* DRAWER - OPEN WHEN isOpen === true */}
            {/* ACCESSORIES DRAWER */}
            {isOpen && (
                <Drawer isOpen={isOpen} placement={'right'} onClose={handleCancel} size={'md'}>
                    <DrawerOverlay />
                    <DrawerContent
                        {...style.drawerContent}
                        style={{
                            top: shouldInsetDrawer ? remCalc(4) : undefined,
                            right: shouldInsetDrawer ? remCalc(4) : undefined,
                            bottom: shouldInsetDrawer ? remCalc(4) : undefined,
                            height: shouldInsetDrawer ? `calc(100vh - ${remCalc(8)})` : undefined,
                        }}
                    >
                        <DrawerHeader {...style.drawerHeader}>
                            <Flex {...style.drawerTitle}>
                                <Text {...style.drawerTitleText}>
                                    <FormattedMessage
                                        id="flexible_bundle.accessories_drawer_title"
                                        defaultMessage="Your {count} accessories"
                                        values={{count: bundleMaxSelectable}}
                                    />
                                </Text>
                            </Flex>
                            <DrawerCloseButton {...style.drawerCloseButton} />
                        </DrawerHeader>
                        <DrawerBody {...style.drawerBody}>
                            {/* PROGRESS BAR */}
                            <FlexibleBundleProgress
                                selectedCount={totalSelectedQuantity}
                                maxSelectable={bundleMaxSelectable}
                            />
                            {/* Selected Accessories (if any) */}
                            {drawerSelectedAccessories.length > 0 && (
                                <VStack w="100%" gap={0}>
                                    {drawerSelectedAccessories.map((selected) => {
                                        const accessory = optionalProducts.find(
                                            (p) => p.productId === selected.productId,
                                        )
                                        return (
                                            <AccessoryTile
                                                key={selected.productId}
                                                accessory={accessory}
                                                variant="quantity"
                                                quantity={selected.quantity}
                                                onQuantityChange={handleChangeAccessoryQuantity}
                                                onRemove={() =>
                                                    handleRemoveAccessory(selected.productId)
                                                }
                                            />
                                        )
                                    })}
                                    <Divider mt={remCalc(12)} />
                                </VStack>
                            )}

                            {/* Available Accessories */}
                            <VStack w="100%" gap={0}>
                                {optionalProducts.map((accessory) => {
                                    // we need to know if an accessory is already selected to avoid adding more than the stock allows.
                                    const selectedAccessory = drawerSelectedAccessories.find(
                                        (item) => item.productId === accessory.productId,
                                    )

                                    const selectedQuantity = selectedAccessory?.quantity || 0

                                    return (
                                        <AccessoryTile
                                            key={accessory.productId}
                                            accessory={accessory}
                                            variant="add"
                                            selectedQuantity={selectedQuantity}
                                            canAddMore={canAddMore}
                                            onAdd={handleAddAccessory}
                                        />
                                    )
                                })}
                            </VStack>
                        </DrawerBody>
                        {/* DRAWER FOOTER WITH BUTTONS */}
                        <DrawerFooter gap={3} justifyContent={'space-between'}>
                            <Box {...style.linkButton}>
                                <Link
                                    onClick={handleCancel}
                                    color="foreground.neutral.default"
                                    fontWeight="medium"
                                    textStyle="body-medium"
                                >
                                    <FormattedMessage
                                        id="flexible_bundle.cancel_button"
                                        defaultMessage="Cancel"
                                    />
                                </Link>
                            </Box>
                            <Button
                                variant="primary"
                                onClick={handleDone}
                                isDisabled={totalSelectedQuantity !== bundleMaxSelectable}
                                {...style.drawerFooterButton}
                            >
                                <FormattedMessage
                                    id="flexible_bundle.done_button"
                                    defaultMessage="Done"
                                />
                            </Button>
                        </DrawerFooter>
                    </DrawerContent>
                </Drawer>
            )}
        </Flex>
    )
}

FlexibleBundle.propTypes = {
    flexibleBundleInfo: PropTypes.shape({
        productSetID: PropTypes.string,
        productSetName: PropTypes.string,
        primaryProducts: PropTypes.arrayOf(
            PropTypes.shape({
                productId: PropTypes.string.isRequired,
                productName: PropTypes.string.isRequired,
                price: PropTypes.number,
                image: PropTypes.string,
                c_productSubtitle: PropTypes.string,
            }),
        ),
        optionalProducts: PropTypes.arrayOf(
            PropTypes.shape({
                productId: PropTypes.string.isRequired,
                productName: PropTypes.string.isRequired,
                price: PropTypes.number,
                image: PropTypes.string,
            }),
        ),
        bundleMaxSelectable: PropTypes.number,
    }),
    selectedAccessories: PropTypes.arrayOf(
        PropTypes.shape({
            productId: PropTypes.string.isRequired,
            quantity: PropTypes.number.isRequired,
        }),
    ),
    onSelectAccessories: PropTypes.func,
    onFocusLinkClick: PropTypes.func,
}

export default FlexibleBundle
